import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import { tokens } from "../../theme";
import { LoaderSpinner } from "../spinner/loader";
import { Num, SectionCard } from "../ui";
import { IconChevronRight, IconPlus, IconSearch } from "../ui/icons";
import { useOperatorMetrics } from "../../hooks/useDashboardMetrics";
import { formatAmountShort } from "../../utils/formatters";
import { ROUTES } from "../layout/navConfig";

moment.locale("es");

const TABS = [
  { id: "pendientes", label: "Pendientes", title: "Tus órdenes pendientes" },
  { id: "firma", label: "Sin firma", title: "Te falta la firma del cliente" },
  { id: "fotos", label: "Sin fotos", title: "Te faltan fotos del trabajo" },
];

/**
 * Inicio del operador. Arranca por la acción (nueva orden), después las tres
 * cifras del día — que además filtran la lista de abajo.
 */
const OperatorHome = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pendientes");
  const { buckets, loading, error, reload } = useOperatorMetrics();

  const goNewOrder = () => {
    localStorage.removeItem("signatureData");
    navigate(ROUTES.newOrder);
  };

  const openOrder = (order) => navigate("/edit-order", { state: { order } });

  const items = buckets[tab] || [];
  const current = TABS.find((t) => t.id === tab) || TABS[0];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", maxWidth: 900 }}>
      {/* La acción primero: es lo que el operador viene a hacer. */}
      <Box
        onClick={goNewOrder}
        sx={{
          backgroundColor: tokens.graphite,
          borderRadius: "14px",
          padding: "16px 18px",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          minHeight: 82,
          cursor: "pointer",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#fff" }}>
            Nueva orden de servicio
          </Typography>
          <Typography sx={{ fontSize: "12px", color: tokens.graphiteInk2, lineHeight: 1.35 }}>
            Cliente, trabajo, fotos y firma en un solo paso
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 999,
            backgroundColor: tokens.brand,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconPlus size={23} />
        </Box>
      </Box>

      {/* Las cifras son los filtros. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
        {TABS.map((t) => {
          const on = t.id === tab;
          return (
            <Box
              component="button"
              type="button"
              key={t.id}
              onClick={() => setTab(t.id)}
              aria-pressed={on}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: "2px",
                minHeight: 68,
                padding: "10px 12px",
                borderRadius: `${tokens.radiusCard}px`,
                textAlign: "left",
                cursor: "pointer",
                fontFamily: "inherit",
                border: `1px solid ${on ? tokens.graphite : tokens.border}`,
                backgroundColor: on ? tokens.graphite : tokens.surface,
              }}
            >
              <Num sx={{ fontSize: "21px", fontWeight: 600, lineHeight: 1, color: on ? "#fff" : tokens.ink }}>
                {loading ? "—" : (buckets[t.id] || []).length}
              </Num>
              <Typography
                sx={{ fontSize: "11.5px", fontWeight: 500, color: on ? tokens.graphiteInk2 : tokens.ink3 }}
              >
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <SectionCard
        title={current.title}
        action={items.length > 0 ? "Ver todas" : undefined}
        onAction={() => navigate(ROUTES.operatorOrders)}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
            <LoaderSpinner />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "28px 16px" }}>
            <Typography sx={{ fontSize: "13.5px", color: tokens.ink2 }}>{error}</Typography>
            <Button variant="outlined" onClick={reload}>
              Reintentar
            </Button>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ padding: "28px 16px", textAlign: "center" }}>
            <Typography sx={{ fontSize: "13.5px", color: tokens.ink3 }}>
              {tab === "pendientes"
                ? "No tenés órdenes pendientes."
                : "Nada pendiente acá. Buen trabajo."}
            </Typography>
          </Box>
        ) : (
          items.slice(0, 10).map((order, index, list) => (
            <Box
              key={order.orderId}
              onClick={() => openOrder(order)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "11px",
                padding: "13px 14px",
                minHeight: 68,
                cursor: "pointer",
                borderBottom: index === list.length - 1 ? "none" : "1px solid #f5f3f1",
                "&:hover": { backgroundColor: tokens.surface2 },
              }}
            >
              <Box
                sx={{
                  width: "3px",
                  alignSelf: "stretch",
                  borderRadius: "2px",
                  flexShrink: 0,
                  backgroundColor: tab === "pendientes" ? "#c8861a" : tokens.brand,
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                  <Num sx={{ fontSize: "12.5px", fontWeight: 600, color: tokens.ink2 }}>
                    #{order.orderId}
                  </Num>
                  <Typography
                    sx={{
                      fontSize: "13.5px",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {order.client?.company || "Sin cliente"}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "12px", color: tokens.ink3 }}>
                  {moment(order.createdAt).format("DD/MM · HH:mm")}
                  {tab === "firma" && " · falta la firma"}
                  {tab === "fotos" && " · faltan fotos"}
                </Typography>
              </Box>
              <Num sx={{ fontSize: "13px", fontWeight: 600, flexShrink: 0 }}>
                {formatAmountShort(order.totalAmount)}
              </Num>
              <Box sx={{ color: tokens.ink4, display: "flex", flexShrink: 0 }}>
                <IconChevronRight size={16} />
              </Box>
            </Box>
          ))
        )}
      </SectionCard>

      <Box
        onClick={() => navigate("/edit-client")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: `${tokens.radiusCard}px`,
          padding: "13px 14px",
          minHeight: 62,
          cursor: "pointer",
          "&:hover": { borderColor: tokens.borderStrong },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "9px",
            backgroundColor: tokens.borderSoft,
            color: tokens.ink2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconSearch size={17} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>Alta de cliente por CUIT</Typography>
          <Typography sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
            Trae razón social y domicilio desde ARCA
          </Typography>
        </Box>
        <Box sx={{ color: tokens.ink4, display: "flex", flexShrink: 0 }}>
          <IconChevronRight size={17} />
        </Box>
      </Box>
    </Box>
  );
};

export default OperatorHome;
